import {
  DynamicSectionBlock,
  getNumRowsFromBlock,
  hasMobilePosition,
  MOBILE,
  ResolvedMargins
} from '@toasttab/sites-components'

import { Block as BlockType, SpacingEnum } from 'src/apollo/sites'

// Returns true if any part of the block exists in the right half of the screen in desktop view.
export const isInRightHalf = (block: BlockType): boolean => {
  return block.endX > MOBILE.NUM_COLS + 1
}

export const spacingEnumToVw = (
  spacing: SpacingEnum | null | undefined
): number => {
  const map = {
    [SpacingEnum.None]: 0,
    [SpacingEnum.Sm]: 4,
    [SpacingEnum.Md]: 6,
    [SpacingEnum.Lg]: 8
  }
  return (spacing && map[spacing]) ?? 0
}

export const resolveHorizontalSpacing = (
  sectionHorizontalMargin: number | null | undefined,
  globalHorizontalSpacing: SpacingEnum | null | undefined
): number => sectionHorizontalMargin ?? spacingEnumToVw(globalHorizontalSpacing)

/**
 * Blocks with an explicit mobile position keep their place.
 * The rest stack after them, left half before right half, top before bottom.
 *
 * A negative value -> a before b
 * A positive value -> a after b
 * Zero or NaN      -> a and b are considered equal
 */
export const sortBlocksForMobile = (blocks: BlockType[]): BlockType[] =>
  [...blocks].sort((a, b) => {
    if (hasMobilePosition(a) && !hasMobilePosition(b)) return -1
    if (!hasMobilePosition(a) && hasMobilePosition(b)) return 1
    if (hasMobilePosition(a) && hasMobilePosition(b)) return 0

    if (isInRightHalf(a) && !isInRightHalf(b)) return 1
    if (!isInRightHalf(a) && isInRightHalf(b)) return -1

    if (a.startY !== b.startY) return a.startY - b.startY
    if (a.startX !== b.startX) return a.startX - b.startX
    return 0
  })

export type MobileOffsets = {
  offsetByGuid: Record<string, number>
  requiredMobileNumRows: number
}

export const computeRequiredNumRows = (blocks: BlockType[]): number =>
  blocks.reduce((rows, block) => Math.max(block.endY - 1, rows), 0)

/**
 * Walks the blocks once to produce both the per-block mobile row offset and the
 * section's mobile height, so the two can never disagree. Blocks without an
 * explicit mobile position stack below the ones that have one.
 */
export const computeMobileOffsets = (
  mobileOrderedBlocks: BlockType[],
  margins: ResolvedMargins,
  maxContentWidth: number | null = null
): MobileOffsets => {
  const rowsUsedByPositionedBlocks = mobileOrderedBlocks
    .filter(hasMobilePosition)
    .reduce((rows, block) => Math.max((block.mobileEndY || 0) - 1, rows), 0)

  const offsetByGuid: Record<string, number> = {}
  let nextOffset = rowsUsedByPositionedBlocks

  mobileOrderedBlocks
    .filter((block) => !hasMobilePosition(block))
    .forEach((block) => {
      offsetByGuid[block.guid] = nextOffset
      nextOffset += getNumRowsFromBlock(block, true, margins, maxContentWidth)
    })

  return { offsetByGuid, requiredMobileNumRows: nextOffset }
}

type BlockRowsAndCols = {
  numRows: number
  numCols: number
  mobileNumRows: number
  mobileNumCols: number
}

export const getBlockRowsAndCols = (
  block: DynamicSectionBlock,
  margins: ResolvedMargins,
  maxContentWidth: number | null = null
): BlockRowsAndCols => {
  const mobileStartX = block.mobileStartX ?? 1
  const mobileEndX = block.mobileEndX ?? MOBILE.NUM_COLS + 1

  return {
    numRows: getNumRowsFromBlock(block, false, margins, maxContentWidth),
    numCols: block.endX - block.startX,
    mobileNumRows: getNumRowsFromBlock(block, true, margins, maxContentWidth),
    mobileNumCols: mobileEndX - mobileStartX
  }
}
